import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { z } from 'zod';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export const notificationPreferenceChannelSchema = z.enum(['EMAIL', 'IN_APP']);
export type NotificationPreferenceChannel = z.infer<
  typeof notificationPreferenceChannelSchema
>;

const notificationPreferenceChannelsSchema = z
  .array(notificationPreferenceChannelSchema)
  .transform((channels) => [...new Set(channels)]);

export const notificationPreferenceOverrideSchema = z.object({
  notificationType: z.string().trim().min(1),
  channels: notificationPreferenceChannelsSchema,
});
export type NotificationPreferenceOverride = z.infer<
  typeof notificationPreferenceOverrideSchema
>;

export const userNotificationPreferencesDataSchema = z.object({
  global: z.object({
    channels: notificationPreferenceChannelsSchema,
    source: z.enum(['DEFAULT', 'EXPLICIT']),
  }),
  overrides: z.array(notificationPreferenceOverrideSchema),
});
export type UserNotificationPreferencesData = z.infer<
  typeof userNotificationPreferencesDataSchema
>;

export const userNotificationPreferencesResponseSchema = z.object({
  success: z.boolean(),
  data: userNotificationPreferencesDataSchema,
});
export type UserNotificationPreferencesResponse = z.infer<
  typeof userNotificationPreferencesResponseSchema
>;

const _mockData: UserNotificationPreferencesResponse = {
  success: true,
  data: {
    global: {
      channels: ['EMAIL', 'IN_APP'],
      source: 'DEFAULT',
    },
    overrides: [
      {
        notificationType: 'DOCUMENT_UPLOADED',
        channels: ['EMAIL', 'IN_APP'],
      },
    ],
  },
};

const getUserNotificationPreferences: QueryFunction<
  UserNotificationPreferencesResponse,
  ['/user/notification-preferences']
> = async () => {
  if (isMockServices()) return await mock(_mockData);

  const authTokenHttp = await getCompatibleAuthTokenHttp();

  const response = await authTokenHttp<UserNotificationPreferencesResponse>(
    `/users/me/notification-preferences`,
    {
      method: 'GET',
    },
  );

  if (response.data) {
    return userNotificationPreferencesResponseSchema.parse(response.data);
  }

  throw new Error('Failed to fetch user notification preferences');
};

export const NOTIFICATION_PREFERENCE_CHANNELS: NotificationPreferenceChannel[] =
  ['EMAIL', 'IN_APP'];

export const DEFAULT_USER_NOTIFICATION_PREFERENCES: UserNotificationPreferencesData =
  {
    global: {
      channels: [...NOTIFICATION_PREFERENCE_CHANNELS],
      source: 'DEFAULT',
    },
    overrides: [],
  };

export const normalizePreferenceChannels = (
  channels: NotificationPreferenceChannel[],
) => {
  const enabled = new Set(channels);

  return NOTIFICATION_PREFERENCE_CHANNELS.filter((channel) =>
    enabled.has(channel),
  );
};

export const arePreferenceChannelsEqual = (
  left: NotificationPreferenceChannel[],
  right: NotificationPreferenceChannel[],
) => {
  const normalizedLeft = normalizePreferenceChannels(left);
  const normalizedRight = normalizePreferenceChannels(right);

  return (
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every((channel, index) => channel === normalizedRight[index])
  );
};

export const resolveNotificationPreferenceEnabled = (
  preferences: UserNotificationPreferencesData,
  notificationType: string,
  channel: NotificationPreferenceChannel,
) => {
  const override = preferences.overrides.find(
    (item) => item.notificationType === notificationType,
  );

  if (override) {
    return override.channels.includes(channel);
  }

  if (preferences.global?.channels) {
    return preferences.global.channels.includes(channel);
  }

  return true;
};

export default getUserNotificationPreferences;
